// geoVINUM app — public landing page.
// Use-case driven (not feature list). Two personas: Amateur + Pro.
// Animated chat mock for hero + embedded preview attempt + strong CTAs.
import { useState, useEffect } from "react";
import {
  Sparkles, ArrowRight, ExternalLink, Globe,
  Camera, Calendar, Gamepad2, GraduationCap, Wine, Store,
  Users, Building2, Compass, MessageSquare, ScanLine, Trophy,
  Send,
} from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import TerroirProDemoForm from "@/components/AudreyAIProDemoForm";
import { useT, useLang } from "@/lib/i18n";

const WEB_APP_URL = "https://app.geovinum.io";

// Pre-baked demo questions to seed conversations in the embedded preview
const DEMO_PROMPTS = [
  { en: "Plan a wine weekend in Burgundy",       fr: "Planifie un week-end vin en Bourgogne",       es: "Planifica un fin de semana en Borgoña",          pt: "Planeie um fim de semana na Borgonha" },
  { en: "Tell me about Châteauneuf-du-Pape",     fr: "Raconte-moi Châteauneuf-du-Pape",             es: "Cuéntame sobre Châteauneuf-du-Pape",             pt: "Conte-me sobre Châteauneuf-du-Pape" },
  { en: "What pairs with a Gigondas?",           fr: "Avec quoi accorder un Gigondas ?",            es: "¿Con qué marida un Gigondas?",                   pt: "Com o que harmoniza um Gigondas?" },
  { en: "Quiz me on Bordeaux grapes",            fr: "Teste-moi sur les cépages bordelais",         es: "Hazme un quiz sobre las variedades de Burdeos",  pt: "Faz-me um quiz sobre castas de Bordéus" },
];

// Short, friendly replies shown in the animated hero chat mock.
const HERO_REPLIES = [
  {
    en: "Got it — 3 days in Beaune. I've built a route: Domaine Faiveley (Sat 10am tasting), lunch at Ma Cuisine, then Pommard cellars in the afternoon. Want me to book?",
    fr: "C'est noté — 3 jours à Beaune. Voici l'itinéraire : Domaine Faiveley (samedi 10h dégustation), déjeuner Ma Cuisine, puis caves de Pommard l'après-midi. Je réserve ?",
    es: "Listo — 3 días en Beaune. Ruta lista: Domaine Faiveley (sábado 10h), comida en Ma Cuisine, bodegas de Pommard por la tarde. ¿Reservo?",
    pt: "Pronto — 3 dias em Beaune. Roteiro pronto: Domaine Faiveley (sábado 10h), almoço Ma Cuisine, caves de Pommard à tarde. Reservo?",
  },
  {
    en: "Châteauneuf-du-Pape: 13 grapes allowed, signature Grenache from sun-baked galets roulés. Power, garrigue, dark cherry. Top vintages: 2019, 2020.",
    fr: "Châteauneuf-du-Pape : 13 cépages autorisés, Grenache star sur les galets roulés. Puissance, garrigue, cerise noire. Top millésimes : 2019, 2020.",
    es: "Châteauneuf-du-Pape: 13 variedades, Garnacha estrella sobre cantos rodados. Potencia, garriga, cereza negra. Mejores añadas: 2019, 2020.",
    pt: "Châteauneuf-du-Pape: 13 castas, Grenache estrela sobre cantos rolados. Potência, garrigue, cereja negra. Melhores safras: 2019, 2020.",
  },
  {
    en: "Try slow-cooked lamb shoulder with rosemary, ratatouille, or aged Comté. The structure of Gigondas loves bold, herbaceous notes.",
    fr: "Essayez un épaule d'agneau confite au romarin, une ratatouille, ou un vieux Comté. La structure du Gigondas adore les notes herbacées.",
    es: "Prueba paletilla de cordero al romero, ratatouille o un Comté añejo. La estructura del Gigondas adora las notas herbáceas.",
    pt: "Experimente pá de borrego com alecrim, ratatouille ou um Comté envelhecido. A estrutura do Gigondas adora notas herbáceas.",
  },
  {
    en: "Round 1 / 5 — Which grape is NOT allowed in red Bordeaux? a) Merlot  b) Pinot Noir  c) Cabernet Franc",
    fr: "Manche 1 / 5 — Quel cépage n'est PAS autorisé en Bordeaux rouge ? a) Merlot  b) Pinot Noir  c) Cabernet Franc",
    es: "Ronda 1 / 5 — ¿Qué variedad NO está permitida en Burdeos tinto? a) Merlot  b) Pinot Noir  c) Cabernet Franc",
    pt: "Ronda 1 / 5 — Que casta NÃO é permitida em Bordéus tinto? a) Merlot  b) Pinot Noir  c) Cabernet Franc",
  },
];

const AmateurIcon = ({ Icon }) => (
  <div className="w-11 h-11 rounded-[5px] bg-[color:var(--gv-pink)]/10 flex items-center justify-center shrink-0">
    <Icon size={20} className="text-[color:var(--gv-pink)]" />
  </div>
);
const ProIcon = ({ Icon }) => (
  <div className="w-11 h-11 rounded-[5px] bg-[color:var(--gv-blue)]/10 flex items-center justify-center shrink-0">
    <Icon size={20} className="text-[color:var(--gv-blue)]" />
  </div>
);

const UseCaseCard = ({ Icon, title, body, variant = "amateur" }) => (
  <div
    data-testid={`terroir-uc-${variant}`}
    className="group relative rounded-[5px] border border-[color:var(--gv-line)] bg-white p-6 hover:border-[color:var(--gv-pink)] hover:shadow-[0_18px_40px_-22px_rgba(46,80,103,0.25)] transition-all"
  >
    <div className="flex items-start gap-4">
      {variant === "amateur" ? <AmateurIcon Icon={Icon} /> : <ProIcon Icon={Icon} />}
      <div className="flex-1">
        <h3 className="font-display text-[19px] text-[color:var(--gv-ink)] leading-tight mb-2">{title}</h3>
        <p className="text-[14px] leading-relaxed text-[color:var(--gv-ink)]/70">{body}</p>
      </div>
    </div>
  </div>
);

const TerroirAIPage = () => {
  const t = useT();
  const lang = useLang();
  const langKey = ["fr", "es", "pt"].includes(lang) ? lang : "en";
  const [demoSeed, setDemoSeed] = useState("");
  const [iframeOk, setIframeOk] = useState(true);

  // Encode the seed into the iframe URL so the embedded app pre-fills the question.
  const iframeSrc = demoSeed
    ? `${WEB_APP_URL}/?embed=1&q=${encodeURIComponent(demoSeed)}`
    : `${WEB_APP_URL}/?embed=1`;

  // Animated hero chat — cycles through the demo prompts
  const [chatStep, setChatStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setChatStep((s) => (s + 1) % DEMO_PROMPTS.length), 4200);
    return () => clearInterval(id);
  }, []);
  const heroPrompt = DEMO_PROMPTS[chatStep][langKey];
  const heroReply = HERO_REPLIES[chatStep][langKey];

  return (
    <Layout testId="terroir-ai-page">
      <SEO
        title={t("terroir.seo.title")}
        description={t("terroir.seo.desc")}
        canonical="/geovinum-ai"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[color:var(--gv-mist)]">
        <div
          className="absolute inset-0 opacity-[0.5] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 0%, rgba(206,26,95,0.10), transparent 55%), radial-gradient(circle at 90% 100%, rgba(46,80,103,0.10), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[color:var(--gv-line)] text-[12px] uppercase tracking-[0.18em] text-[color:var(--gv-ink)]/70 mb-7">
              <Sparkles size={13} className="text-[color:var(--gv-pink)]" />
              {t("terroir.hero.eyebrow")}
            </div>
            <h1
              className="font-display text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.02] tracking-[-0.01em] text-[color:var(--gv-ink)]"
              data-testid="terroir-hero-h1"
            >
              {t("terroir.hero.h1_a")}
              <br />
              <span className="text-[color:var(--gv-pink)]">{t("terroir.hero.h1_b")}</span>
            </h1>
            <p className="mt-7 max-w-xl text-[16.5px] leading-relaxed text-[color:var(--gv-ink)]/75">
              {t("terroir.hero.sub")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href={WEB_APP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="terroir-cta-web"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[14px] font-medium hover:bg-[color:var(--gv-pink-dark)] transition-colors"
              >
                <Globe size={15} /> {t("terroir.hero.cta_primary")} <ArrowRight size={15} />
              </a>
              <a
                href={`${WEB_APP_URL}/auth/register`}
                target="_blank"
                rel="noreferrer"
                data-testid="terroir-cta-signup"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-[5px] bg-[color:var(--gv-ink)] text-white text-[14px] font-medium hover:bg-[color:var(--gv-blue)] transition-colors"
              >
                {t("terroir.hero.cta_secondary")} <ArrowRight size={15} />
              </a>
            </div>
            <p className="mt-5 text-[12.5px] text-[color:var(--gv-ink)]/55">
              {t("terroir.hero.pwa_note")}
            </p>
          </div>

          {/* Animated chat mock — pure CSS, brand-aligned, no external deps */}
          <div className="relative">
            <div
              className="relative mx-auto aspect-[9/16] max-w-[360px] rounded-[5px] border border-[color:var(--gv-line)] bg-white overflow-hidden shadow-[0_40px_80px_-30px_rgba(46,80,103,0.35)] flex flex-col"
              data-testid="terroir-hero-mock"
            >
              {/* Top bar */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--gv-line)] bg-white">
                <div className="w-7 h-7 rounded-full bg-[color:var(--gv-pink)] flex items-center justify-center">
                  <Sparkles size={13} className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-[12px] font-semibold text-[color:var(--gv-ink)]">geoVINUM app</div>
                  <div className="text-[10px] text-[color:var(--gv-ink)]/55">geoVINUM · online</div>
                </div>
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
              </div>
              {/* Messages */}
              <div className="flex-1 px-4 py-5 space-y-3 overflow-hidden bg-[color:var(--gv-mist)]/40">
                <div
                  key={`u-${chatStep}`}
                  style={{ animation: "fadeInUp 0.5s ease-out both" }}
                  className="ml-auto max-w-[78%] px-3.5 py-2.5 rounded-[5px] bg-[color:var(--gv-ink)] text-white text-[12.5px] leading-snug shadow-sm"
                >
                  {heroPrompt}
                </div>
                <div
                  key={`a-${chatStep}`}
                  style={{ animation: "fadeInUp 0.7s 0.35s ease-out both" }}
                  className="max-w-[88%] px-3.5 py-2.5 rounded-[5px] bg-white text-[color:var(--gv-ink)] text-[12.5px] leading-snug border border-[color:var(--gv-line)] shadow-sm"
                >
                  {heroReply}
                </div>
                <div
                  style={{ animation: "fadeInUp 0.4s 0.15s ease-out both" }}
                  className="flex items-center gap-1.5 text-[color:var(--gv-ink)]/40"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gv-pink)]/70" style={{ animation: "bounce 1.2s 0s infinite" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gv-pink)]/70" style={{ animation: "bounce 1.2s 0.15s infinite" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gv-pink)]/70" style={{ animation: "bounce 1.2s 0.3s infinite" }} />
                </div>
              </div>
              {/* Input bar */}
              <div className="px-3 py-3 border-t border-[color:var(--gv-line)] bg-white">
                <div className="flex items-center gap-2 px-3 py-2 rounded-[5px] border border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/50">
                  <Camera size={14} className="text-[color:var(--gv-ink)]/45" />
                  <span className="flex-1 text-[11.5px] text-[color:var(--gv-ink)]/45">Ask anything…</span>
                  <div className="w-6 h-6 rounded-[5px] bg-[color:var(--gv-pink)] flex items-center justify-center">
                    <Send size={11} className="text-white" />
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[72%] h-7 rounded-full bg-[color:var(--gv-ink)]/15 blur-2xl" />
          </div>
        </div>
      </section>

      {/* Inline keyframes scoped to this page */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* DEMO STRIP — seeded prompts */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h2 className="font-display text-[34px] leading-tight text-[color:var(--gv-ink)]">{t("terroir.demo.title")}</h2>
            <p className="mt-2 max-w-2xl text-[15px] text-[color:var(--gv-ink)]/70">{t("terroir.demo.sub")}</p>
          </div>
          <a
            href={WEB_APP_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-[13.5px] text-[color:var(--gv-pink)] hover:underline"
            data-testid="terroir-demo-newtab"
          >
            {t("terroir.demo.open_in_new")} <ExternalLink size={13} />
          </a>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          {DEMO_PROMPTS.map((p, i) => (
            <button
              key={i}
              onClick={() => setDemoSeed(p[langKey])}
              data-testid={`terroir-demo-prompt-${i}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-[5px] border text-[13px] transition-colors ${
                demoSeed === p[langKey]
                  ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)] text-white"
                  : "border-[color:var(--gv-line)] bg-white text-[color:var(--gv-ink)] hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)]"
              }`}
            >
              <MessageSquare size={13} />
              {p[langKey]}
            </button>
          ))}
        </div>
        <div className="rounded-[5px] border border-[color:var(--gv-line)] overflow-hidden bg-white relative" data-testid="terroir-demo-frame">
          {iframeOk ? (
            <>
              <iframe
                key={iframeSrc}
                src={iframeSrc}
                title="geoVINUM app — live demo"
                className="w-full h-[640px] bg-white"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                onError={() => setIframeOk(false)}
              />
              {/* Visible overlay link in case iframe is blocked by remote headers */}
              <a
                href={iframeSrc}
                target="_blank"
                rel="noreferrer"
                className="absolute bottom-3 right-3 inline-flex items-center gap-2 px-3 py-2 rounded-[5px] bg-white border border-[color:var(--gv-line)] text-[12px] text-[color:var(--gv-ink)] hover:text-[color:var(--gv-pink)] hover:border-[color:var(--gv-pink)] shadow-sm"
              >
                {t("terroir.demo.open_in_new")} <ExternalLink size={12} />
              </a>
            </>
          ) : (
            <div className="h-[480px] flex flex-col items-center justify-center px-6 text-center bg-[color:var(--gv-mist)]/50">
              <Sparkles size={32} className="text-[color:var(--gv-pink)] mb-4" />
              <p className="text-[15px] text-[color:var(--gv-ink)]/75 max-w-md mb-6">
                {t("terroir.hero.sub")}
              </p>
              <a
                href={WEB_APP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[14px] font-medium hover:bg-[color:var(--gv-pink-dark)]"
              >
                <Globe size={15} /> {t("terroir.hero.cta_primary")} <ArrowRight size={15} />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* USE CASES — AMATEUR */}
      <section className="bg-white border-t border-[color:var(--gv-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-2xl mb-12">
            <h2 className="font-display text-[36px] sm:text-[44px] leading-tight text-[color:var(--gv-ink)]">
              {t("terroir.usecases.title")}
            </h2>
            <p className="mt-3 text-[15.5px] text-[color:var(--gv-ink)]/70">{t("terroir.usecases.sub")}</p>
          </div>

          <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--gv-pink)]/10 text-[11.5px] uppercase tracking-[0.18em] text-[color:var(--gv-pink)] font-medium">
            <Wine size={12} /> {t("terroir.uc.amateur.tag")}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <UseCaseCard Icon={Compass}  variant="amateur" title={t("terroir.uc.amateur.1.title")} body={t("terroir.uc.amateur.1.body")} />
            <UseCaseCard Icon={Camera}   variant="amateur" title={t("terroir.uc.amateur.2.title")} body={t("terroir.uc.amateur.2.body")} />
            <UseCaseCard Icon={Calendar} variant="amateur" title={t("terroir.uc.amateur.3.title")} body={t("terroir.uc.amateur.3.body")} />
            <UseCaseCard Icon={Gamepad2} variant="amateur" title={t("terroir.uc.amateur.4.title")} body={t("terroir.uc.amateur.4.body")} />
          </div>
        </div>
      </section>

      {/* USE CASES — PRO */}
      <section className="bg-[color:var(--gv-mist)] border-t border-[color:var(--gv-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--gv-blue)]/10 text-[11.5px] uppercase tracking-[0.18em] text-[color:var(--gv-blue)] font-medium">
            <Building2 size={12} /> {t("terroir.uc.pro.tag")}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <UseCaseCard Icon={Wine}          variant="pro" title={t("terroir.uc.pro.1.title")} body={t("terroir.uc.pro.1.body")} />
            <UseCaseCard Icon={Store}         variant="pro" title={t("terroir.uc.pro.2.title")} body={t("terroir.uc.pro.2.body")} />
            <UseCaseCard Icon={Users}         variant="pro" title={t("terroir.uc.pro.3.title")} body={t("terroir.uc.pro.3.body")} />
            <UseCaseCard Icon={GraduationCap} variant="pro" title={t("terroir.uc.pro.4.title")} body={t("terroir.uc.pro.4.body")} />
            <UseCaseCard Icon={Building2}    variant="pro" title={t("terroir.uc.pro.5.title")} body={t("terroir.uc.pro.5.body")} />
            <div className="rounded-[5px] border border-dashed border-[color:var(--gv-blue)]/40 bg-white p-6 flex flex-col justify-between">
              <div>
                <div className="w-11 h-11 rounded-[5px] bg-[color:var(--gv-blue)] flex items-center justify-center mb-4">
                  <Trophy size={20} className="text-white" />
                </div>
                <h3 className="font-display text-[19px] text-[color:var(--gv-ink)] leading-tight mb-2">
                  {t("terroir.audiences.pro.title")}
                </h3>
                <p className="text-[14px] leading-relaxed text-[color:var(--gv-ink)]/70">
                  {t("terroir.audiences.pro.body")}
                </p>
              </div>
              <a
                href="#pro-demo"
                data-testid="terroir-pro-contact"
                className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-[5px] bg-[color:var(--gv-blue)] text-white text-[13px] font-medium hover:bg-[color:var(--gv-blue-dark)] transition-colors w-fit"
              >
                {t("terroir.audiences.pro.cta")} <ArrowRight size={13} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PRO DEMO FORM — B2B lead capture */}
      <TerroirProDemoForm />

      {/* HOW IT WORKS */}
      <section className="bg-white border-t border-[color:var(--gv-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <h2 className="font-display text-[34px] leading-tight text-[color:var(--gv-ink)] mb-12">
            {t("terroir.how.title")}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { Icon: Globe,        title: t("terroir.how.1.title"), body: t("terroir.how.1.body") },
              { Icon: ScanLine,     title: t("terroir.how.2.title"), body: t("terroir.how.2.body") },
              { Icon: GraduationCap,title: t("terroir.how.3.title"), body: t("terroir.how.3.body") },
            ].map((s, i) => (
              <div key={i} className="relative rounded-[5px] border border-[color:var(--gv-line)] p-7">
                <div className="text-[12px] uppercase tracking-[0.22em] text-[color:var(--gv-ink)]/40 mb-4">0{i + 1}</div>
                <s.Icon size={26} className="text-[color:var(--gv-pink)] mb-4" />
                <h3 className="font-display text-[20px] text-[color:var(--gv-ink)] mb-2">{s.title}</h3>
                <p className="text-[14.5px] leading-relaxed text-[color:var(--gv-ink)]/70">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-[color:var(--gv-ink)] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-24 text-center">
          <h2 className="font-display text-[38px] sm:text-[48px] leading-tight mb-4">{t("terroir.cta.title")}</h2>
          <p className="text-[15.5px] text-white/75 mb-9">{t("terroir.cta.sub")}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={WEB_APP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="terroir-closing-web"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[14px] font-medium hover:bg-[color:var(--gv-pink-dark)] transition-colors"
            >
              <Globe size={15} /> {t("terroir.cta.web")} <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TerroirAIPage;
